import { useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  BanknotesIcon,
  LockClosedIcon,
  MapPinIcon,
  PencilSquareIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { CheckIcon, ClockIcon } from '@heroicons/react/20/solid';
import customFetch from '@utils/customFetch';
import { Link, redirect, useLoaderData, useNavigate } from 'react-router-dom';
import { useCartContext } from '@context/cartContext';
import SEO from '@utils/seo';
import {
  FAQ,
  POLICIES,
  SHOP_FACTS,
  TECH_SPECS,
  absoluteUrl,
  formatPrice,
  getProductContent,
} from './produktContent';
import ProductFaq from './ProductFaq';
import BusinessTeaser from './BusinessTeaser';

const POLICY_ICONS = {
  banknotes: BanknotesIcon,
  mapPin: MapPinIcon,
  pencil: PencilSquareIcon,
  lock: LockClosedIcon,
};

const CTA_CLASS =
  'flex w-full items-center justify-center rounded-md border border-transparent bg-svisit-primary-600 px-8 py-3 text-base font-medium text-white hover:bg-svisit-primary-700 focus:outline-hidden focus:ring-2 focus:ring-svisit-primary-500 focus:ring-offset-2 focus:ring-offset-gray-50';

export const loader = async ({ params }) => {
  try {
    const { data } = await customFetch.get(`/products/${params.id}`);
    return data;
  } catch (error) {
    return redirect('/');
  }
};

// Hauptbild mit Thumbnail-Leiste; bei nur einem Bild entfällt die Leiste.
const Gallery = ({ images }) => {
  const [active, setActive] = useState(0);
  const current = images[active] ?? images[0];
  return (
    <div>
      <div className="aspect-[3/2] w-full overflow-hidden rounded-lg bg-gray-50">
        <img
          src={current.src}
          alt={current.alt}
          className="h-full w-full object-cover object-center"
        />
      </div>
      {images.length > 1 && (
        <div className="mt-4 grid grid-cols-4 gap-4">
          {images.map((image, index) => (
            <button
              key={image.src}
              type="button"
              onClick={() => setActive(index)}
              aria-label={image.alt}
              aria-pressed={index === active}
              className={`aspect-[3/2] overflow-hidden rounded-md bg-gray-50 ring-2 ring-offset-2 ${
                index === active
                  ? 'ring-svisit-primary-600'
                  : 'ring-transparent hover:ring-gray-300'
              }`}
            >
              <img
                src={image.src}
                alt=""
                className="h-full w-full object-cover object-center"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ProductDetail = () => {
  const data = useLoaderData();
  const product = data.product;
  const content = getProductContent(product);
  const { addToCart } = useCartContext();
  const navigate = useNavigate();

  // "Custom gestalten" öffnet den Karten-Designer (allgemein freigeschaltet),
  // Basic wandert direkt in den Warenkorb.
  const handleCta = () => {
    if (product.hasCustomization) {
      navigate(`/karten-designer/${product._id}`);
      return;
    }
    addToCart(product, 1, product.graphicFront, product.graphicBack);
    navigate('/warenkorb');
  };

  const inStock = product.inventory > 0;
  const description = content.intro ?? product.description;
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description,
    sku: product._id,
    brand: { '@type': 'Brand', name: 'sVisit' },
    image: content.gallery.map((image) => absoluteUrl(image.src)),
    offers: {
      '@type': 'Offer',
      url: window.location.href,
      priceCurrency: 'CHF',
      price: (product.price / 100).toFixed(2),
      availability: inStock
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
      itemCondition: 'https://schema.org/NewCondition',
    },
  };

  return (
    <>
      <SEO
        title={`${product.name} von sVisit - Die digitale Visitenkarte.`}
        description={`${content.tagline}. ${description}`}
        type="website"
        image={absoluteUrl(content.gallery[0].src)}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <main className="py-24">
        {/* Kaufblock + Galerie */}
        <div className="mx-auto max-w-2xl px-4 pt-16 sm:px-6 sm:pt-24 lg:grid lg:max-w-7xl lg:grid-cols-2 lg:gap-x-12 lg:px-8">
          <div className="lg:max-w-lg">
            <nav aria-label="Breadcrumb">
              <Link
                to="/produkte"
                className="text-sm font-medium text-gray-500 hover:text-gray-900"
              >
                ← Zurück zu allen Produkten
              </Link>
            </nav>

            {content.badge && (
              <span className="mt-6 inline-block rounded-full bg-red-50 px-3 py-1 text-xs font-semibold text-svisit-primary-700 ring-1 ring-inset ring-svisit-primary-200">
                {content.badge}
              </span>
            )}
            <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              {product.name}
            </h1>
            <p className="mt-2 text-lg text-gray-600">{content.tagline}</p>

            <section aria-labelledby="information-heading" className="mt-6">
              <h2 id="information-heading" className="sr-only">
                Produktinformationen
              </h2>
              <p className="flex items-baseline gap-x-2">
                <span className="text-3xl font-bold tracking-tight text-gray-900">
                  {formatPrice(product.price)}
                </span>
                <span className="text-sm text-gray-500">einmalig, kein Abo</span>
              </p>
              {description && (
                <p className="mt-6 text-base text-gray-600">{description}</p>
              )}
              {content.features.length > 0 && (
                <ul role="list" className="mt-6 space-y-2">
                  {content.features.map((feature) => (
                    <li
                      key={feature}
                      className="flex items-start text-sm text-gray-700"
                    >
                      <CheckIcon
                        className="mr-2 mt-0.5 h-5 w-5 shrink-0 text-svisit-primary-600"
                        aria-hidden="true"
                      />
                      {feature}
                    </li>
                  ))}
                </ul>
              )}

              <div className="mt-8 space-y-2 border-t border-gray-100 pt-6 text-sm text-gray-600">
                <p className="flex items-center">
                  {inStock ? (
                    <CheckIcon
                      className="mr-2 h-5 w-5 shrink-0 text-green-500"
                      aria-hidden="true"
                    />
                  ) : (
                    <ClockIcon
                      className="mr-2 h-5 w-5 shrink-0 text-gray-300"
                      aria-hidden="true"
                    />
                  )}
                  {inStock
                    ? `Verfügbar – Lieferung in ${SHOP_FACTS.deliveryTime}n`
                    : 'Aktuell nicht auf Lager'}
                </p>
                <p className="pl-7">
                  {SHOP_FACTS.printedIn} · Standardversand{' '}
                  {SHOP_FACTS.shippingStandard}, Expressversand{' '}
                  {SHOP_FACTS.shippingExpress}
                </p>
              </div>
            </section>

            <section aria-labelledby="options-heading" className="mt-10">
              <h2 id="options-heading" className="sr-only">
                Bestellen
              </h2>
              <button type="button" onClick={handleCta} className={CTA_CLASS}>
                {content.cta}
              </button>
              <p className="mt-6 flex items-center justify-center text-base text-gray-500">
                <ShieldCheckIcon
                  className="mr-2 h-6 w-6 shrink-0 text-gray-400"
                  aria-hidden="true"
                />
                Sichere Zahlungsabwicklung – {SHOP_FACTS.payment}
              </p>
            </section>
          </div>

          <div className="mt-10 lg:mt-0 lg:self-start">
            <Gallery images={content.gallery} />
          </div>
        </div>

        <div className="mx-auto max-w-2xl px-4 py-24 sm:px-6 sm:py-32 lg:max-w-7xl lg:px-8">
          {/* Highlights */}
          {content.highlights.length > 0 && (
            <section aria-labelledby="details-heading">
              <div className="flex flex-col items-center text-center">
                <h2
                  id="details-heading"
                  className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl"
                >
                  Was ist inklusive?
                </h2>
                <p className="mt-3 max-w-3xl text-lg text-gray-600">
                  Mit deiner sVisit-Karte erhältst du nicht nur eine Karte,
                  sondern dein digitales Visitenkarten-Profil – vollständig nach
                  deinen Vorstellungen gestaltbar und jederzeit änderbar.
                </p>
              </div>
              <div className="mt-16 space-y-20">
                {content.highlights.map((highlight, index) => (
                  <div
                    key={highlight.title}
                    className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16"
                  >
                    <div
                      className={`aspect-[3/2] w-full overflow-hidden rounded-lg bg-gray-50 ${
                        index % 2 === 1 ? 'lg:order-2' : ''
                      }`}
                    >
                      <img
                        src={highlight.image}
                        alt={highlight.imageAlt}
                        loading="lazy"
                        className="h-full w-full object-cover object-center"
                      />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold tracking-tight text-gray-900">
                        {highlight.title}
                      </h3>
                      <p className="mt-4 text-base text-gray-600">
                        {highlight.text}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Technische Details */}
          <section
            aria-labelledby="specs-heading"
            className="mt-24 grid grid-cols-1 gap-10 lg:grid-cols-3"
          >
            <div className="lg:col-span-2">
              <h2
                id="specs-heading"
                className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl"
              >
                Technische Details
              </h2>
              <dl className="mt-8 grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
                {TECH_SPECS.map((spec) => (
                  <div key={spec.label} className="border-t border-gray-200 pt-4">
                    <dt className="text-sm font-medium text-gray-900">
                      {spec.label}
                    </dt>
                    <dd className="mt-1 text-sm text-gray-600">{spec.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
            {content.printSpecs && (
              <div className="rounded-2xl bg-gray-50 p-8">
                <h3 className="text-lg font-semibold text-gray-900">
                  Druck &amp; Format
                </h3>
                <ul role="list" className="mt-4 space-y-3">
                  {content.printSpecs.map((spec) => (
                    <li
                      key={spec}
                      className="flex items-start text-sm text-gray-700"
                    >
                      <CheckIcon
                        className="mr-2 mt-0.5 h-5 w-5 shrink-0 text-svisit-primary-600"
                        aria-hidden="true"
                      />
                      {spec}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>

          {/* Policies */}
          <section aria-labelledby="policy-heading" className="mt-24">
            <h2 id="policy-heading" className="sr-only">
              Unsere Leistungen
            </h2>
            <div className="grid grid-cols-1 gap-y-12 sm:grid-cols-2 sm:gap-x-6 lg:grid-cols-4 lg:gap-x-8">
              {POLICIES.map((policy) => {
                const Icon = POLICY_ICONS[policy.icon];
                return (
                  <div key={policy.name}>
                    <Icon
                      className="h-12 w-12 text-svisit-primary-500"
                      aria-hidden="true"
                    />
                    <h3 className="mt-6 text-base font-medium text-gray-900">
                      {policy.name}
                    </h3>
                    <p className="mt-3 text-base text-gray-500">
                      {policy.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Cross-Sell */}
          {content.crossSell && (
            <section
              aria-labelledby="crosssell-heading"
              className="mt-24 rounded-2xl bg-gray-50 px-6 py-10 sm:px-10 lg:flex lg:items-center lg:justify-between"
            >
              <div className="lg:max-w-2xl">
                <h2
                  id="crosssell-heading"
                  className="text-2xl font-bold tracking-tight text-gray-900"
                >
                  {content.crossSell.title}
                </h2>
                <p className="mt-2 text-base text-gray-600">
                  {content.crossSell.text}
                </p>
              </div>
              <Link
                to={content.crossSell.to}
                className="mt-6 inline-block rounded-md bg-white px-5 py-3 text-base font-semibold text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:bg-gray-100 lg:mt-0"
              >
                {content.crossSell.cta}
              </Link>
            </section>
          )}

          <div className="mt-24">
            <BusinessTeaser />
          </div>

          <div className="mx-auto mt-24 max-w-3xl">
            <ProductFaq items={FAQ} />
          </div>
        </div>
      </main>
    </>
  );
};

export default ProductDetail;
